import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  HelpCircle,
  Download,
  FileCheck,
  Settings,
  CheckCircle2,
  ExternalLink,
  Copy,
  FolderOpen,
  RefreshCw,
  AlertTriangle,
  Monitor,
  Shield,
  Smartphone,
  Usb,
  Wifi,
  Play,
} from "lucide-react";
import { toast } from "sonner";
import { isAndroidDevice } from "@/lib/rawbt-print";

const CERTIFICATE_CONTENT = `-----BEGIN CERTIFICATE-----
MIIDFzCCAf+gAwIBAgIUUNe3sSzzfmbfwj8PBdNlIsYMNEkwDQYJKoZIhvcNAQEL
BQAwGzEZMBcGA1UEAwwQc2VqYWFsdHVzLmNvbS5icjAeFw0yNjAzMDQwMDMwNTVa
Fw0zNjAzMDEwMDMwNTVaMBsxGTAXBgNVBAMMEHNlamFhbHR1cy5jb20uYnIwggEi
MA0GCSqGSIb3DQEBAQUAA4IBDwAwggEKAoIBAQDRZxpDd4FP+NXrLxHjjKt+i9BP
wULn0VLrj4QWT1omZrtnxYacts+zmY0I18M5cj5616TxgIsTTvw2QQOpxRAmO1Fu
OvJMTOUW85Wf3cPLVegnvQ98DjBN5sMH7TuZ1KRJWqcdFh2OvjztVmtjMaAjYQqd
aucotqQS2o7Rx53nr4NvllvGgNX2s6VQVOrCtytcXsMTLjEVnjOSwWmJaZyMzUrj
ZzuGznB5n1fRByaW0HdHywGHmZOjBqOodeHVi6xORVKcKTNBKNFwIsRyfE54oHdB
olRTps6QXrrPkWs+JXo9kaDV6wbl5jNihiAJpOBEZV151SIpNZ3xOg2XrBWpAgMB
AAGjUzBRMB0GA1UdDgQWBBQs4LEYv+i9Zor5tffg/zXGNbmeLTAfBgNVHSMEGDAW
gBQs4LEYv+i9Zor5tffg/zXGNbmeLTAPBgNVHRMBAf8EBTADAQH/MA0GCSqGSIb3
DQEBCwUAA4IBAQBNR8AKxgknTsqJO/PSGV0kY4naIsPRroZIKk5oHSwchwMd8VcD
8UVX3/7/sVvJoi7JCDvUDpm7JAX8/ETAOHmQPuONgYMES4N5FPWJo8Jvg4oCXeMV
dluVEHXZSGBIfjU4Izx4kg3UyM39LkT/ZE6GfFlnSEzOy/iAQvfqgOim5Ami8E/Z
WOMkH90bD6CPeEU1fpx6hgUlrKVSsfODyB0222CMcsI7hoU/HpEAXRcN7MA0c739
HA/UZnPfQJisIOv91vdzgfkvnqdadUYSU0pWrxRlhgM66G2ocv21oyVFoBjWw95n
BizUK00Wl5psclpv2gaXmEcM53Ay/sIg34cZ
-----END CERTIFICATE-----`;

export function QZTraySetupGuide() {
  const [open, setOpen] = useState(false);
  const defaultTab = isAndroidDevice() ? "rawbt" : "qztray";

  const handleCopyCertificate = () => {
    navigator.clipboard.writeText(CERTIFICATE_CONTENT);
    toast.success("Certificado copiado para a área de transferência!");
  };

  const handleDownloadCertificate = () => {
    const blob = new Blob([CERTIFICATE_CONTENT], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "override.crt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Arquivo override.crt baixado!");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <HelpCircle className="h-4 w-4" />
          Guia de Instalação
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Settings className="h-5 w-5" />
            Guia de Configuração de Impressão
          </DialogTitle>
          <DialogDescription>
            Escolha o modo de impressão adequado ao seu dispositivo
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue={defaultTab} className="py-2">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="qztray" className="gap-2">
              <Monitor className="h-4 w-4" />
              QZ Tray (Windows/Mac)
            </TabsTrigger>
            <TabsTrigger value="rawbt" className="gap-2">
              <Smartphone className="h-4 w-4" />
              RawBT (Android)
            </TabsTrigger>
          </TabsList>

          {/* QZ Tray Tab */}
          <TabsContent value="qztray">
            <QZTraySteps
              onDownloadCertificate={handleDownloadCertificate}
              onCopyCertificate={handleCopyCertificate}
            />
          </TabsContent>

          {/* RawBT Tab */}
          <TabsContent value="rawbt">
            <RawBTSteps />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

/* ── QZ Tray Steps ── */
function QZTraySteps({
  onDownloadCertificate,
  onCopyCertificate,
}: {
  onDownloadCertificate: () => void;
  onCopyCertificate: () => void;
}) {
  return (
    <div className="space-y-6 py-4">
      {/* Step 1 - Download */}
      <Card className="border-l-4 border-l-primary">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-3 text-lg">
            <Badge variant="default" className="h-7 w-7 rounded-full p-0 flex items-center justify-center">1</Badge>
            <Download className="h-5 w-5 text-primary" />
            Baixar o QZ Tray
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Baixe e instale o QZ Tray no computador que será usado para impressão.
          </p>
          <Button variant="default" className="gap-2" onClick={() => window.open("https://qz.io/download/", "_blank")}>
            <ExternalLink className="h-4 w-4" />
            Baixar QZ Tray (qz.io)
          </Button>
          <Alert>
            <Monitor className="h-4 w-4" />
            <AlertDescription>
              <strong>Requisitos:</strong> Windows 7 ou superior, macOS 10.10+, ou Linux.
              O QZ Tray deve ser instalado em cada computador que fará impressões.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Step 2 - Certificate */}
      <Card className="border-l-4 border-l-orange-500">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-3 text-lg">
            <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-orange-500">2</Badge>
            <Shield className="h-5 w-5 text-orange-500" />
            Instalar o Certificado
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            O certificado permite que o sistema se comunique com segurança com o QZ Tray.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="default" className="gap-2" onClick={onDownloadCertificate}>
              <Download className="h-4 w-4" />
              Baixar override.crt
            </Button>
            <Button variant="outline" className="gap-2" onClick={onCopyCertificate}>
              <Copy className="h-4 w-4" />
              Copiar Conteúdo
            </Button>
          </div>
          <Separator />
          <div className="space-y-3">
            <h4 className="font-medium flex items-center gap-2">
              <FolderOpen className="h-4 w-4" />
              Onde colocar o arquivo:
            </h4>
            <div className="space-y-2 text-sm">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium text-foreground">Windows:</p>
                <code className="text-xs bg-background p-1 rounded block mt-1">
                  C:\Program Files\QZ Tray\override\override.crt
                </code>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium text-foreground">macOS:</p>
                <code className="text-xs bg-background p-1 rounded block mt-1">
                  /Applications/QZ Tray.app/Contents/Resources/override/override.crt
                </code>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium text-foreground">Linux:</p>
                <code className="text-xs bg-background p-1 rounded block mt-1">
                  /opt/qz-tray/override/override.crt
                </code>
              </div>
            </div>
            <Alert variant="destructive" className="bg-destructive/10 border-destructive/20">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Importante:</strong> Se a pasta <code className="bg-background px-1 rounded">override</code> não existir,
                você deve criá-la manualmente dentro da pasta de instalação do QZ Tray.
              </AlertDescription>
            </Alert>
          </div>
        </CardContent>
      </Card>

      {/* Step 3 - Restart */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-3 text-lg">
            <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-blue-500">3</Badge>
            <RefreshCw className="h-5 w-5 text-blue-500" />
            Reiniciar o QZ Tray
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Após copiar o certificado, reinicie o QZ Tray para carregar as novas configurações.
          </p>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <span>Clique com o botão direito no ícone do QZ Tray na bandeja do sistema</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <span>Selecione <strong>"Exit"</strong> ou <strong>"Sair"</strong></span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <span>Abra o QZ Tray novamente pelo menu Iniciar</span>
            </div>
          </div>
          <Alert>
            <Monitor className="h-4 w-4" />
            <AlertDescription>
              O ícone do QZ Tray aparece na <strong>bandeja do sistema</strong> (área de notificação),
              geralmente no canto inferior direito da tela.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Step 4 - Test */}
      <Card className="border-l-4 border-l-green-500">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-3 text-lg">
            <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-green-500">4</Badge>
            <FileCheck className="h-5 w-5 text-green-500" />
            Testar a Configuração
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Volte para esta página e adicione uma nova impressora para testar a conexão.
          </p>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-500" />
              <span>Clique em <strong>"Nova Impressora"</strong></span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-500" />
              <span>Se a lista de impressoras carregar, a configuração está correta!</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-500" />
              <span>Selecione sua impressora térmica e salve</span>
            </div>
          </div>
          <Alert className="bg-green-500/10 border-green-500/20">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <AlertDescription className="text-green-700 dark:text-green-300">
              <strong>Dica:</strong> Para impressoras térmicas, recomendamos usar drivers
              genéricos como "Generic / Text Only" ou o driver específico do fabricante.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Troubleshooting */}
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Problemas Comuns
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="space-y-2">
            <p className="font-medium">A lista de impressoras não carrega:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
              <li>Verifique se o QZ Tray está rodando (ícone na bandeja)</li>
              <li>Confirme que o arquivo override.crt está na pasta correta</li>
              <li>Reinicie o QZ Tray após adicionar o certificado</li>
              <li>Verifique se o navegador não está bloqueando WebSockets</li>
            </ul>
          </div>
          <div className="space-y-2">
            <p className="font-medium">Erro de certificado/assinatura:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
              <li>Baixe novamente o arquivo override.crt desta página</li>
              <li>Certifique-se de que o arquivo não foi modificado</li>
              <li>A pasta deve se chamar exatamente "override" (sem acento)</li>
            </ul>
          </div>
          <div className="space-y-2">
            <p className="font-medium">Impressora não aparece na lista:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
              <li>Verifique se a impressora está instalada no Windows/macOS</li>
              <li>Teste imprimindo uma página de teste pelo sistema</li>
              <li>Clique no botão de atualizar na lista de impressoras</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── RawBT Steps ── */
function RawBTSteps() {
  return (
    <div className="space-y-6 py-4">
      {/* Step 1 - Download RawBT */}
      <Card className="border-l-4 border-l-primary">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-3 text-lg">
            <Badge variant="default" className="h-7 w-7 rounded-full p-0 flex items-center justify-center">1</Badge>
            <Download className="h-5 w-5 text-primary" />
            Instalar o App RawBT
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Instale o aplicativo RawBT no dispositivo Android que será usado para impressão.
          </p>
          <Button
            variant="default"
            className="gap-2"
            onClick={() => window.open("https://play.google.com/store/apps/details?id=ru.a402d.rawbtprinter", "_blank")}
          >
            <ExternalLink className="h-4 w-4" />
            Baixar RawBT (Play Store)
          </Button>
          <Alert>
            <Smartphone className="h-4 w-4" />
            <AlertDescription>
              <strong>Compatível com:</strong> Android 5.0 ou superior.
              Funciona com terminais Sunmi, tablets e celulares Android.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Step 2 - Configure USB */}
      <Card className="border-l-4 border-l-orange-500">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-3 text-lg">
            <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-orange-500">2</Badge>
            <Usb className="h-5 w-5 text-orange-500" />
            Configurar Impressora (USB)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Para impressoras conectadas via cabo USB ao dispositivo Android:
          </p>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <span>Abra o app <strong>RawBT</strong></span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <span>Em <strong>"Connection type"</strong>, selecione <strong>"USB"</strong></span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <span>Conecte a impressora via cabo USB e aceite a permissão de acesso</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <span>Toque em <strong>"Test print"</strong> para verificar</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step 2b - Configure Network */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-3 text-lg">
            <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-blue-500">
              <Wifi className="h-3.5 w-3.5" />
            </Badge>
            <Wifi className="h-5 w-5 text-blue-500" />
            Alternativa: Impressora de Rede (TCP/IP)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Para impressoras conectadas na mesma rede Wi-Fi do dispositivo:
          </p>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <span>No app RawBT, selecione <strong>"TCP/IP"</strong> como tipo de conexão</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <span>Insira o <strong>IP da impressora</strong> e a <strong>porta</strong> (geralmente 9100)</span>
            </div>
          </div>
          <div className="p-3 bg-muted rounded-lg text-sm">
            <p className="font-medium text-foreground">Exemplo:</p>
            <code className="text-xs bg-background p-1 rounded block mt-1">
              IP: 192.168.1.100 &nbsp;|&nbsp; Porta: 9100
            </code>
          </div>
          <Alert>
            <Wifi className="h-4 w-4" />
            <AlertDescription>
              O IP da impressora pode ser encontrado imprimindo uma <strong>página de autoconfiguração</strong> da própria impressora
              (geralmente segurando o botão de alimentação por 5 segundos).
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Step 3 - Select mode in system */}
      <Card className="border-l-4 border-l-purple-500">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-3 text-lg">
            <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-purple-500">3</Badge>
            <Settings className="h-5 w-5 text-purple-500" />
            Selecionar Modo no Sistema
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Volte para a página de Impressoras no sistema e altere o modo de impressão.
          </p>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-purple-500" />
              <span>No card <strong>"Modo de Impressão"</strong>, selecione <strong>"RawBT (Android)"</strong></span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-purple-500" />
              <span>Clique em <strong>"Salvar"</strong> para confirmar a alteração</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step 4 - Test */}
      <Card className="border-l-4 border-l-green-500">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-3 text-lg">
            <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-green-500">4</Badge>
            <Play className="h-5 w-5 text-green-500" />
            Testar Impressão
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Use o botão de teste no card "Modo de Impressão" para enviar uma página de teste.
          </p>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-500" />
              <span>Clique em <strong>"Imprimir Página de Teste"</strong></span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-500" />
              <span>O navegador abrirá brevemente o app RawBT — isto é normal</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-500" />
              <span>A impressora deve imprimir a página de teste automaticamente</span>
            </div>
          </div>
          <Alert className="bg-green-500/10 border-green-500/20">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <AlertDescription className="text-green-700 dark:text-green-300">
              <strong>Dica:</strong> Após o teste, volte ao Chrome manualmente.
              Cada impressão alterna brevemente o foco para o RawBT.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Troubleshooting */}
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Problemas Comuns
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="space-y-2">
            <p className="font-medium">O app RawBT não abre ao imprimir:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
              <li>Verifique se o RawBT está instalado e atualizado</li>
              <li>Nas configurações do Android, permita que o RawBT abra links</li>
              <li>Reinicie o dispositivo e tente novamente</li>
            </ul>
          </div>
          <div className="space-y-2">
            <p className="font-medium">Impressora não responde:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
              <li>Verifique a conexão USB ou o IP/porta da rede</li>
              <li>Use o botão "Test print" dentro do app RawBT</li>
              <li>Para USB: desconecte e reconecte o cabo, aceitando a permissão</li>
              <li>Para rede: confirme que impressora e dispositivo estão na mesma Wi-Fi</li>
            </ul>
          </div>
          <div className="space-y-2">
            <p className="font-medium">Chrome perde o foco a cada impressão:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
              <li>Isto é esperado — o RawBT precisa abrir brevemente para processar</li>
              <li>Em terminais Sunmi, o retorno ao Chrome é quase instantâneo</li>
              <li>Considere fixar o Chrome na tela para facilitar o retorno</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
